import * as React from "react";
import clsx from "clsx";
import { Divider, FormHelperText, MenuItem, Radio, Select, Typography, makeStyles, Theme } from "@material-ui/core";
import { Competition } from "../../types/competition";
import { HandicapAdjustmentMode, HandicapSourceType } from "../../types/game";
import {
  GAME_RULE_LISTING_ID,
  WHS_LISTING_ID,
  WHS_LISTING_SHORT_LABEL,
  customRuleListingOptions,
} from "../../utils/handicapListings";

const LISTING_GAME_RULE = GAME_RULE_LISTING_ID;
const LISTING_WHS_HI = WHS_LISTING_ID;

const ADJUSTMENT_OPTIONS: {
  value: HandicapAdjustmentMode;
  title: string;
  description: string;
}[] = [
  {
    value: "none",
    title: "Scoring only",
    description: "Use this listing for scoring only.",
  },
  {
    value: "apply_source_rules",
    title: "Adjust from results",
    description: "Update that rule from each player's score.",
  },
];

export interface HandicapSourceFieldsProps {
  handicapSourceType?: HandicapSourceType;
  handicapSourceCompetitionId?: string;
  handicapAdjustmentMode?: HandicapAdjustmentMode;
  competitions: Competition[];
  ruleId?: string;
  allowExternalCustomListings?: boolean;
  onHandicapListingChange: (sourceType: HandicapSourceType, sourceCompetitionId?: string) => void;
  onApplyRulesToggle: (value: HandicapAdjustmentMode) => void;
  errListing?: boolean;
  errAdjustment?: boolean;
  listingHint?: string;
  savePrompt?: string;
}

const HandicapSourceFields: React.FunctionComponent<HandicapSourceFieldsProps> = props => {
  const classes = useStyles();
  const {
    handicapSourceType = "game_rule",
    handicapSourceCompetitionId = "",
    handicapAdjustmentMode,
    competitions,
    ruleId,
    allowExternalCustomListings = false,
    errListing,
    errAdjustment,
    listingHint = "Which handicaps this game uses.",
    savePrompt = "Pick one option.",
  } = props;

  const customRules = allowExternalCustomListings ? customRuleListingOptions(competitions, ruleId) : [];
  const gameRuleLabel = competitions.find(c => c._id === ruleId)?.name ?? "Game rule";
  const listingHintText = allowExternalCustomListings
    ? listingHint
    : "This rule or WHS Index only.";

  const listingValue =
    handicapSourceType === "custom_rule" && handicapSourceCompetitionId
      ? handicapSourceCompetitionId
      : handicapSourceType === "whs_hi"
        ? LISTING_WHS_HI
        : LISTING_GAME_RULE;

  const handleListingChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value as string;
    if (value === LISTING_GAME_RULE) {
      props.onHandicapListingChange("game_rule");
      return;
    }
    if (value === LISTING_WHS_HI) {
      props.onHandicapListingChange("whs_hi");
      return;
    }
    props.onHandicapListingChange("custom_rule", value);
  };

  const canChooseAdjustment = handicapSourceType === "custom_rule" && !!handicapSourceCompetitionId;
  const selectedMode = handicapAdjustmentMode ?? "";

  const adjustmentUnavailableHint = (() => {
    if (!allowExternalCustomListings) {
      return "Standard rules cannot use another custom listing.";
    }
    if (customRules.length === 0) {
      return "No other custom rules available.";
    }
    return "Pick a custom rule in Handicap listing first.";
  })();

  return (
    <>
      <div className={classes.row}>
        <div className={classes.inline}>
          <Typography variant={"subtitle1"} className={classes.rowLabel}>
            Handicap listing
          </Typography>
          <Select
            className={classes.listSelect}
            value={listingValue}
            onChange={handleListingChange}
            displayEmpty
            error={!!errListing}
          >
            <MenuItem value={LISTING_GAME_RULE}>{gameRuleLabel}</MenuItem>
            <MenuItem value={LISTING_WHS_HI}>{WHS_LISTING_SHORT_LABEL}</MenuItem>
            {customRules.length > 0 ? <Divider className={classes.listDivider} /> : null}
            {customRules.map(rule => (
              <MenuItem key={rule.id} value={rule.id}>
                {rule.label}
              </MenuItem>
            ))}
          </Select>
        </div>
        <Typography variant="body2" color="textSecondary" className={classes.hint}>
          {listingHintText}
        </Typography>
      </div>
      {errListing ? (
        <FormHelperText error className={classes.errorHint}>
          Select a listing.
        </FormHelperText>
      ) : null}

      <div className={classes.row}>
        <Typography variant={"subtitle1"} className={classes.sectionLabel}>
          Adjust handicaps after game
        </Typography>
        {canChooseAdjustment ? (
          <>
            <div className={classes.cardGroup} role="radiogroup" aria-label="Adjust handicaps after game">
              {ADJUSTMENT_OPTIONS.map(option => {
                const selected = selectedMode === option.value;
                const selectOption = () => props.onApplyRulesToggle(option.value);
                return (
                  <div
                    key={option.value}
                    role="radio"
                    aria-checked={selected}
                    tabIndex={0}
                    className={clsx(
                      classes.card,
                      selected && classes.cardSelected,
                      errAdjustment && !selectedMode && classes.cardError,
                    )}
                    onClick={selectOption}
                    onKeyDown={event => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        selectOption();
                      }
                    }}
                  >
                    <Radio
                      color="primary"
                      checked={selected}
                      className={classes.cardRadio}
                      disableRipple
                      tabIndex={-1}
                    />
                    <div className={classes.cardContent}>
                      <Typography variant="subtitle2" className={classes.cardTitle}>
                        {option.title}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" className={classes.cardDescription}>
                        {option.description}
                      </Typography>
                    </div>
                  </div>
                );
              })}
            </div>
            {!selectedMode ? (
              <Typography variant="body2" color="textSecondary" className={classes.hint}>
                {savePrompt}
              </Typography>
            ) : null}
          </>
        ) : (
          <Typography variant="body2" color="textSecondary" className={classes.hint}>
            {adjustmentUnavailableHint}
          </Typography>
        )}
        {errAdjustment ? (
          <FormHelperText error className={classes.errorHint}>
            Pick an option above.
          </FormHelperText>
        ) : null}
      </div>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  row: {
    marginBottom: theme.spacing(2),
  },
  inline: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
  },
  sectionLabel: {
    display: "block",
    marginBottom: theme.spacing(1),
  },
  rowLabel: {
    flex: 1,
    paddingRight: theme.spacing(1),
  },
  listSelect: {
    minWidth: 180,
    maxWidth: 240,
  },
  listDivider: {
    margin: theme.spacing(1, 0),
  },
  hint: {
    marginTop: theme.spacing(0.5),
    lineHeight: 1.45,
  },
  errorHint: {
    marginTop: theme.spacing(0.5),
    marginBottom: 0,
  },
  cardGroup: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: theme.spacing(1.5),
  },
  card: {
    display: "flex",
    alignItems: "flex-start",
    width: "100%",
    textAlign: "left",
    padding: theme.spacing(1, 1.5, 1.5, 0.5),
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    cursor: "pointer",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
    "&:hover": {
      borderColor: theme.palette.primary.light,
    },
    "&:focus": {
      outline: "none",
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 2px ${theme.palette.primary.main}33`,
    },
  },
  cardRadio: {
    padding: theme.spacing(1),
    pointerEvents: "none",
  },
  cardContent: {
    flex: 1,
    paddingTop: theme.spacing(1.25),
    paddingRight: theme.spacing(0.5),
  },
  cardSelected: {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.type === "dark" ? "rgba(25, 118, 210, 0.12)" : "rgba(25, 118, 210, 0.06)",
  },
  cardError: {
    borderColor: theme.palette.error.light,
  },
  cardTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  cardDescription: {
    lineHeight: 1.45,
  },
}));

export default HandicapSourceFields;
